import React, { useEffect, useState } from 'react';
import { Marker, Source, Layer, MarkerDragEvent } from 'react-map-gl/maplibre';
import MapComponent from '../components/Map';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../store/store';
import {
  Box,
  Button,
  IconButton,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';
import useAuthHeader from 'react-auth-kit/hooks/useAuthHeader';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { setMunicipality } from '../redux/municipialitySlice';
import { getMunicipality } from '../hooks/GetMunicipality';
import { addMessage } from '../redux/infoMessageSlice';
import { getUsersWithoutMunicipality } from '../hooks/GetUserWithoutMunicipality';
import { getUsersWithinMunicipality } from '../hooks/GetUserWithinMunicipality';

interface AddMunicipalityProps {
  closeFunction?: () => void;
}

const AddMunicipality: React.FC<AddMunicipalityProps> = ({ closeFunction }) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const auth: string = useAuthHeader() || '';
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const longitude = useSelector((state: RootState) => state.map.longitude);
  const latitude = useSelector((state: RootState) => state.map.latitude);
  const municipality = useSelector(
    (state: RootState) => state.municipality.selectedMunicipality
  );
  const [isAuthorized, setIsAuthorized] = useState<boolean>(false);

  useEffect(() => {
    const verifyAccess = async () => {
      const username = localStorage.getItem('username');

      if (!username) {
        dispatch(addMessage({ message: 'error.unauthorized' }));
        navigate('/');
        return;
      }

      try {
        let hasAccess = false;

        const withoutResult = await getUsersWithoutMunicipality(auth, dispatch);
        if (Array.isArray(withoutResult)) {
          const openUserData = withoutResult.find(
            (u) => u.user.username === username
          );
          if (openUserData && openUserData.role === 'admin') {
            hasAccess = true;
          }
        }

        if (!hasAccess && municipality?.id) {
          const withinResult = await getUsersWithinMunicipality(auth, dispatch);
          if (Array.isArray(withinResult)) {
            const currentUserData = withinResult.find(
              (u) => u.user.username === username
            );
            if (
              currentUserData &&
              ['manager', 'admin'].includes(currentUserData.role)
            ) {
              hasAccess = true;
            }
          }
        }

        if (hasAccess) {
          setIsAuthorized(true);
        } else {
          dispatch(addMessage({ message: 'error.unauthorized' }));
          navigate('/');
        }
      } catch (error) {
        console.error('Authorization check failed', error);
        dispatch(addMessage({ message: 'error.unauthorized' }));
        navigate('/');
      }
    };

    verifyAccess();
  }, [auth, dispatch, navigate, municipality?.id]);

  const parseCoordinate = (
    value: number | string | undefined,
    fallback: number
  ) => {
    const parsed = typeof value === 'string' ? Number(value) : value;
    if (parsed === undefined || parsed === null || Number.isNaN(parsed)) {
      return fallback;
    }
    return parseFloat(parsed.toFixed(7));
  };

  const [boundingBox, setBoundingBox] = useState({
    min_longitude: 0,
    min_latitude: 0,
    max_longitude: 0,
    max_latitude: 0,
  });
  const [originalBoundingBox, setOriginalBoundingBox] = useState<{
    min_longitude: number;
    min_latitude: number;
    max_longitude: number;
    max_latitude: number;
  } | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [markers, setMarkers] = useState([
    {
      id: 1,
      lng: parseCoordinate(municipality?.max_longitude, longitude + 0.01),
      lat: parseCoordinate(municipality?.max_latitude, latitude + 0.01),
    },
    {
      id: 2,
      lng: parseCoordinate(municipality?.min_longitude, longitude - 0.01),
      lat: parseCoordinate(municipality?.max_latitude, latitude + 0.01),
    },
    {
      id: 3,
      lng: parseCoordinate(municipality?.min_longitude, longitude - 0.01),
      lat: parseCoordinate(municipality?.min_latitude, latitude - 0.01),
    },
    {
      id: 4,
      lng: parseCoordinate(municipality?.max_longitude, longitude + 0.01),
      lat: parseCoordinate(municipality?.min_latitude, latitude - 0.01),
    },
  ]);
  const [name, setName] = useState(municipality?.name || '');
  const [file, setFile] = useState<File>();
  const [fileError, setFileError] = useState<string>('');
  const { REACT_APP_API_URL } = process.env;
  const [missingName, setMissingName] = useState<boolean>(false);

  const handleMarkerDragEnd = (event: MarkerDragEvent, id: number) => {
    const { lng, lat } = event.lngLat;
    setMarkers((prevMarkers) =>
      prevMarkers.map((marker) =>
        marker.id === id
          ? {
              ...marker,
              lng: parseFloat(lng.toFixed(7)),
              lat: parseFloat(lat.toFixed(7)),
            }
          : marker
      )
    );
  };
  const hasBoundingBoxChanged = () => {
    if (!originalBoundingBox) return true; // For new municipalities, always include bounding box
    return (
      boundingBox.min_longitude !== originalBoundingBox.min_longitude ||
      boundingBox.min_latitude !== originalBoundingBox.min_latitude ||
      boundingBox.max_longitude !== originalBoundingBox.max_longitude ||
      boundingBox.max_latitude !== originalBoundingBox.max_latitude
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (name == '') {
      setMissingName(true);
      return;
    }
    if (fileError) {
      return;
    }
    setLoading(true);
    let method: string;
    let url: string;
    if (municipality) {
      method = 'PUT';
      url = `${REACT_APP_API_URL}/municipality/${municipality.id}/`;
    } else {
      method = 'POST';
      url = `${REACT_APP_API_URL}/municipality/`;
    }

    try {
      const formData = new FormData();
      formData.append('name', name);

      // Only include bounding box data if it has changed (for editing) or if it's a new municipality
      if (hasBoundingBoxChanged()) {
        formData.append('min_latitude', boundingBox.min_latitude.toFixed(7));
        formData.append('min_longitude', boundingBox.min_longitude.toFixed(7));
        formData.append('max_latitude', boundingBox.max_latitude.toFixed(7));
        formData.append('max_longitude', boundingBox.max_longitude.toFixed(7));
      }

      if (file) {
        formData.append('logo', file);
      }

      const response = await fetch(url, {
        method: method,
        headers: {
          Authorization: auth,
        },
        body: formData,
      });

      if (!response.ok) {
        let errorText = `${response.status} ${response.statusText}`;
        try {
          const responseBody = await response.json();
          if (responseBody && responseBody.detail) {
            errorText = responseBody.detail;
          }
        } catch {
          // Ignore JSON errors
        }

        dispatch(
          addMessage({
            message: 'error.failedToFetch',
            error: errorText,
          })
        );
        return;
      }

      setName('');
      setMissingName(false);
      const fetchedMunicipality = await getMunicipality(auth, dispatch);
      if (fetchedMunicipality) {
        dispatch(setMunicipality(fetchedMunicipality));
      }
      if (closeFunction) {
        closeFunction();
      }
    } catch (error) {
      dispatch(
        addMessage({
          message: 'error.failedToFetch',
          error: (error as Error).name,
        })
      );
    } finally {
      setLoading(false);
    }
  };
  const lineData = {
    type: 'Feature',
    geometry: {
      type: 'Polygon',
      coordinates: [
        [
          ...markers.map((marker) => [marker.lng, marker.lat]),
          [markers[0].lng, markers[0].lat], // Get first marker coordinates
        ],
      ],
    },
  };

  const roundCoordinate = (value: number) => parseFloat(value.toFixed(7));

  const calculateBoundingBox = () => {
    const longitudes = markers.map((marker) => marker.lng);
    const latitudes = markers.map((marker) => marker.lat);

    return {
      min_longitude: roundCoordinate(Math.min(...longitudes)),
      max_longitude: roundCoordinate(Math.max(...longitudes)),
      min_latitude: roundCoordinate(Math.min(...latitudes)),
      max_latitude: roundCoordinate(Math.max(...latitudes)),
    };
  };

  const validateLogoFile = (file: File): string => {
    const allowedTypes = [
      'image/jpeg',
      'image/jpg',
      'image/png',
      'image/gif',
      'image/webp',
      'image/bmp',
    ];
    const allowedExtensions = [
      '.jpg',
      '.jpeg',
      '.png',
      '.gif',
      '.webp',
      '.bmp',
    ];
    const extension = file.name.toLowerCase().split('.').pop() || '';
    const hasValidExtension = allowedExtensions.includes(`.${extension}`);
    if (!allowedTypes.includes(file.type) && !hasValidExtension) {
      return t('municipality.invalidLogoType');
    }
    if (file.size > 1024 * 1024) {
      // 1 MB max size
      return t('municipality.invalidLogoSize');
    }
    return '';
  };

  const handleFileChange = (file: File) => {
    const validationError = validateLogoFile(file);
    if (validationError) {
      setFile(undefined);
      setFileError(validationError);
      return;
    }
    setFile(file);
    setFileError('');
  };
  const handleRemoveFile = () => {
    setFile(undefined);
    setFileError('');
  };
  const handleCancel = () => {
    if (closeFunction) {
      closeFunction();
    } else {
      navigate('/');
    }
  };

  useEffect(() => {
    const newBoundingBox = calculateBoundingBox();
    setBoundingBox(newBoundingBox);

    // Store original bounding box for editing mode
    if (municipality && !originalBoundingBox) {
      setOriginalBoundingBox({
        min_longitude: municipality.min_longitude,
        min_latitude: municipality.min_latitude,
        max_longitude: municipality.max_longitude,
        max_latitude: municipality.max_latitude,
      });
    }
  }, [markers, municipality]);

  if (!isAuthorized) {
    return null;
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Navbar />
      <Box
        sx={{
          marginTop: 4,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          flex: 1,
        }}
      >
        <Typography component="h1" variant="h5">
          {t('municipality.create')}
        </Typography>
        <Box
          component="form"
          onSubmit={handleSubmit}
          noValidate
          sx={{
            mt: 2,
            display: 'flex',
            flexDirection: isMobile ? 'column' : 'row',
            width: '90%',
            height: '100%',
            justifyContent: 'center',
          }}
        >
          <Box
            sx={{
              mr: '24px',
              width: isMobile ? '100%' : '25%',
              height: '80%',
            }}
          >
            <TextField
              fullWidth
              label={t('municipality.name')}
              name="name"
              autoComplete="name"
              autoFocus
              type="text"
              id="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              sx={{ mb: 0.5 }}
            />
            {missingName && (
              <Box sx={{ width: '100%', display: 'flex', ml: 1 }}>
                <Typography sx={{ color: 'red', mb: 0.5 }}>
                  {t('municipality.missingName')}
                </Typography>
              </Box>
            )}
            <Typography variant="h6" sx={{ mt: 2 }}>
              {t('municipality.addLogo')}
            </Typography>
            <Box
              sx={{
                maxHeight: '35%',
                overflowY: 'auto',
                paddingRight: 1,
                marginTop: 1,
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  mt: 1,
                }}
              >
                <Box
                  sx={{
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    padding: '4px 4px 4px 8px',
                    width: isMobile ? '85%' : '90%',
                  }}
                >
                  <Typography
                    sx={{
                      width: isMobile ? '65%' : '100%',
                      overflow: 'auto',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {file?.name || t('municipality.noLogo')}
                  </Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    component="label"
                    sx={{
                      boxShadow: '0',
                      '&:hover': {
                        boxShadow: '0',
                      },
                    }}
                  >
                    {t('municipality.browse')}
                    <input
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp,image/bmp"
                      hidden
                      onChange={(e) => {
                        if (e.target.files && e.target.files[0]) {
                          handleFileChange(e.target.files[0]);
                        }
                      }}
                    />
                  </Button>
                </Box>
                <IconButton onClick={() => handleRemoveFile()}>
                  <DeleteIcon />
                </IconButton>
              </Box>
              {fileError && (
                <Typography sx={{ color: 'red', mt: 1 }}>
                  {fileError}
                </Typography>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              width: isMobile ? '100%' : '50%',
              display: 'flex',
              flexDirection: 'column',
              height: '100%',
            }}
          >
            <Box
              sx={{
                height: isMobile ? 'auto' : '80%',
                width: '100%',
              }}
            >
              <div style={{ width: '100%', height: isMobile ? '40vh' : '90%' }}>
                <MapComponent
                  onBoundingBoxChange={() => {}}
                  onZoomChange={() => {}}
                >
                  {markers.map((marker) => (
                    <Marker
                      key={marker.id}
                      longitude={marker.lng}
                      latitude={marker.lat}
                      draggable
                      onDragEnd={(e) => handleMarkerDragEnd(e, marker.id)}
                    />
                  ))}

                  <Source id="polygon-source" type="geojson" data={lineData}>
                    <Layer
                      id="polygon-layer"
                      type="fill"
                      paint={{
                        'fill-color': '#007cbf',
                        'fill-opacity': 0.4,
                      }}
                    />
                    <Layer
                      id="line-layer"
                      type="line"
                      paint={{
                        'line-color': '#007cbf',
                        'line-width': 2,
                      }}
                    />
                  </Source>
                </MapComponent>
              </div>

              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  justifyContent: 'right',
                  alignItems: 'end',
                  mt: '12px',
                  mb: '12px',
                  height: isMobile ? 'auto' : '10%',
                }}
              >
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  disabled={loading}
                  sx={{ marginRight: 2 }}
                >
                  {t('municipality.save')}
                </Button>
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleCancel}
                >
                  {t('municipality.abort')}
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
export default AddMunicipality;
